import type { BaseComponentProps } from '@json-render/react';
import { useQuery } from '@tanstack/react-query';
import OilyticsClient from 'api/OilyticsClient';
import DaysToExpiryChartInner from 'components/days-to-expiry-chart';
import ForwardCurve from 'components/forward-curve';
import PercentileChartInner from 'components/percentile-chart';
import SeasonalityLW from 'components/seasonality';
import React from 'react';
import type { z } from 'zod';

import type {
  DaysToExpiryChartProps as DaysToExpiryChartSchema,
  ForwardCurveChartProps as ForwardCurveChartSchema,
  PercentileChartProps as PercentileChartSchema,
  SeasonalityChartProps as SeasonalityChartSchema,
} from './catalog';

type SeasonalityChartProps = z.infer<typeof SeasonalityChartSchema>;
type ForwardCurveChartProps = z.infer<typeof ForwardCurveChartSchema>;
type PercentileChartProps = z.infer<typeof PercentileChartSchema>;
type DaysToExpiryChartProps = z.infer<typeof DaysToExpiryChartSchema>;

/**
 * Shared hook — fetches trade chart data from `/trades?formula=...&chart_type=...`
 * the same way `use-watchlist.ts` does for historic trades (see
 * use-watchlist.ts:386). Anything the formula parser accepts works here, so
 * passing a bare symbol like `CME:CL.M1!` or a formula expression are both valid.
 *
 * Returns the API response `data` untyped (`any`) because each inner chart
 * component declares its own local TradeData interface and does runtime
 * validation; imposing a shared type here would force a second source of truth.
 */
function useSymbolChartData (
  chartType: 'seasonality' | 'forward_curve' | 'percentile' | 'days_to_expiry',
  symbol: string | undefined,
  frequency?: 'D' | 'W' | 'M',
  periods?: number,
  dates?: string[],
) {
  return useQuery({
    queryKey: ['genui-symbol-chart', chartType, symbol ?? '', frequency ?? null, periods ?? null, dates ?? null],
    queryFn: async (): Promise<any> => {
      if (!symbol) return null;
      const client = await OilyticsClient.create();
      const resp = await client.getTradePreview(symbol, chartType, frequency, periods, dates);
      return resp?.data ?? null;
    },
    enabled: Boolean(symbol),
  });
}

function NoSymbolCard () {
  return (
    <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
      No symbol provided
    </div>
  );
}

function ChartShell ({
  title,
  height,
  children,
}: {
  title?: string;
  height: number;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      {title && <div className="text-sm font-semibold text-foreground mb-1">{title}</div>}
      <div style={{ height }}>
        {children}
      </div>
    </div>
  );
}

/**
 * SeasonalityChart — GenUI wrapper around the in-app SeasonalityLW chart.
 * Fetches chart data from the `symbol` prop (via /trades?formula=...) instead
 * of accepting inline data. Mirrors the symbol-only pattern used by
 * TradingViewChart.
 */
export const SeasonalityChart = ({
  props,
}: BaseComponentProps<SeasonalityChartProps>) => {
  const { symbol, title } = props;
  const height = props.height ?? 500;
  const { data, isLoading } = useSymbolChartData('seasonality', symbol);

  if (!symbol) return <NoSymbolCard />;

  return (
    <ChartShell title={title} height={height}>
      <SeasonalityLW tradeData={data ?? undefined} loading={isLoading} />
    </ChartShell>
  );
};

/**
 * ForwardCurveChart — GenUI wrapper around the in-app ForwardCurve chart.
 * Accepts optional frequency/periods or explicit dates to pull historical
 * curves. The interactive callbacks (onApplyDates/onAddTradeDate/onResetDates)
 * exist for the app's toggle UI — they're intentionally unset here because a
 * GenUI dashboard is a static snapshot.
 */
export const ForwardCurveChart = ({
  props,
}: BaseComponentProps<ForwardCurveChartProps>) => {
  const { symbol, title, frequency, periods, dates } = props;
  const height = props.height ?? 500;
  // Only forward frequency to the API when paired with periods — matches the
  // guard in use-watchlist.ts:378-380.
  const normalizedFrequency = frequency === 'D' || frequency === 'W' || frequency === 'M'
    ? frequency
    : undefined;
  const { data, isLoading } = useSymbolChartData(
    'forward_curve',
    symbol,
    normalizedFrequency && periods ? normalizedFrequency : undefined,
    normalizedFrequency && periods ? periods : undefined,
    dates,
  );

  if (!symbol) return <NoSymbolCard />;

  return (
    <ChartShell title={title} height={height}>
      <ForwardCurve tradeData={data ?? undefined} loading={isLoading} />
    </ChartShell>
  );
};

/**
 * PercentileChart — GenUI wrapper around the in-app PercentileChart.
 * (Imported as PercentileChartInner to avoid a name collision with the
 * exported wrapper.)
 */
export const PercentileChart = ({
  props,
}: BaseComponentProps<PercentileChartProps>) => {
  const { symbol, title } = props;
  const height = props.height ?? 500;
  const { data, isLoading } = useSymbolChartData('percentile', symbol);

  if (!symbol) return <NoSymbolCard />;

  return (
    <ChartShell title={title} height={height}>
      <PercentileChartInner tradeData={data ?? undefined} loading={isLoading} />
    </ChartShell>
  );
};

/**
 * DaysToExpiryChart — GenUI wrapper around the in-app DaysToExpiryChart.
 * The backend's `days_to_expiry` chart_type already returns 25 vintage series
 * (past 12 + current + next 12), so this wrapper just forwards the symbol.
 */
export const DaysToExpiryChart = ({
  props,
}: BaseComponentProps<DaysToExpiryChartProps>) => {
  const { symbol, title } = props;
  const height = props.height ?? 500;
  const { data, isLoading } = useSymbolChartData('days_to_expiry', symbol);

  if (!symbol) return <NoSymbolCard />;

  return (
    <ChartShell title={title} height={height}>
      <DaysToExpiryChartInner tradeData={data ?? undefined} loading={isLoading} />
    </ChartShell>
  );
};
